import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';

// --- Helper functions (getDaysInMonth, AreaGradient, etc.) remain unchanged ---
function getDaysInMonth(month, year) { /* ... no changes ... */ }
function AreaGradient({ color, id }) { /* ... no changes ... */ }
function calculateTrendPercentage(data) { /* ... no changes ... */ }
function determineTrend(percentage) { /* ... no changes ... */ }
function formatTrendValue(percentage) { /* ... no changes ... */ }


// --- COMPONENT LOGIC ---
// **MODIFIED**: Added a default empty array for the 'data' prop
function StatCard({ title, value, interval, trend: providedTrend, data = [] }) {
  const theme = useTheme();
  const daysInWeek = getDaysInMonth(4, 2024);

  // **NEW**: A simple check to see if we have data for the chart and trend
  const hasChartData = data && data.length > 0;

  // These calculations will only run if hasChartData is true
  const trendPercentage = hasChartData ? calculateTrendPercentage(data) : 0;
  const trend = hasChartData ? (providedTrend || determineTrend(trendPercentage)) : 'neutral';
  const trendValue = hasChartData ? formatTrendValue(trendPercentage) : '...';

  const trendColors = {
    up: theme.palette.mode === 'light' ? theme.palette.error.main : theme.palette.error.dark,
    down: theme.palette.mode === 'light' ? theme.palette.success.main : theme.palette.success.dark,
    neutral: theme.palette.mode === 'light' ? theme.palette.grey[400] : theme.palette.grey[700],
  };

  const labelColors = {
    up: 'error',
    down: 'success',
    neutral: 'default',
  };

  const color = labelColors[trend];
  const chartColor = trendColors[trend];

  return (
    <Card variant="outlined" sx={{ height: '100%', flexGrow: 1 }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        <Stack
          direction="column"
          sx={{ justifyContent: 'space-between', flexGrow: '1', gap: 1 }}
        >
          <Stack sx={{ justifyContent: 'space-between' }}>
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="h4" component="p">
                {value}
              </Typography>
              {/* **MODIFIED**: Only show the Chip if there is chart data */}
              {hasChartData && <Chip size="small" color={color} label={trendValue} />}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {interval}
            </Typography>
          </Stack>
          {/* **MODIFIED**: Only show the SparkLineChart if there is chart data */}
          {hasChartData ? (
            <Box sx={{ width: '100%', height: 50 }}>
              <SparkLineChart
                colors={[chartColor]}
                data={data}
                area
                showHighlight
                showTooltip
                xAxis={{
                  scaleType: 'band',
                  data: daysInWeek,
                }}
                sx={{
                  [`& .${areaElementClasses.root}`]: {
                    fill: `url(#area-gradient-${value})`,
                  },
                }}
              >
                <AreaGradient color={chartColor} id={`area-gradient-${value}`} />
              </SparkLineChart>
            </Box>
          ) : (
            // Render a placeholder to maintain layout consistency
            <Box sx={{ width: '100%', height: 50 }} />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

StatCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number),
  interval: PropTypes.string, // Also made interval optional
  title: PropTypes.string.isRequired,
  trend: PropTypes.oneOf(['down', 'neutral', 'up']),
  value: PropTypes.string.isRequired,
};

export default StatCard;